import { config } from './config';
import type { ShareAssetODI } from 'src/types/TShare';

type GetThumbnailParams = {
  width: number;
  height: number;
  id: string;
  jwt?: string;
  /** The asset's thumbnail version (`thumbnailVersion(asset)`); see below. */
  version?: number;
};

/**
 * The asset's thumbnail version: `addons_info.ON_DEMAND_IMAGE.version` from
 * /data or a search `extended_result` entry. It goes up whenever the thumbnail
 * changes — a custom upload, a storyboard frame, a revert, regeneration after
 * the file is replaced.
 */
export const thumbnailVersion = (
  asset?: { addons_info?: { ON_DEMAND_IMAGE?: { version?: number } } } | null,
): number | undefined => asset?.addons_info?.ON_DEMAND_IMAGE?.version || undefined;

export const getThumbnailUrl = ({ width, height, id, jwt, version }: GetThumbnailParams) => {
  // _ctx=ui flags this as a dashboard (owner) request so the CDN Worker logs it
  // with context='ui' and the receiver excludes it from billing + delivery
  // analytics. The Worker drops _ctx from the cache key, so it never fragments
  // the shared edge cache. Public /shared pages use getODIThumbnailUrl instead
  // and are intentionally not tagged.
  //
  // `v` names WHICH thumbnail the URL means, so a changed thumbnail is a new
  // URL: a miss in the browser cache AND at the edge, where the CDN Worker keys
  // ODI entries by it (`__v`). Without it the old image survives for the
  // browser's 24h max-age, and the edge's, since a purge can't reach every
  // resize variant. The origin ignores the param.
  const versionParam = version ? `&v=${version}` : '';
  const baseUrl = `${config.cdnUrl}/api/v1/assets/${id}/conversions?resize=${width},${height}&_ctx=ui${versionParam}`;
  if (jwt) {
    return `${baseUrl}&_jwt=${jwt.replace('Bearer ', '')}`;
  }
  return baseUrl;
};

/**
 * Watermark configuration for ODI URLs.
 * Uses the FileSpin watermark query parameter format:
 * &watermark={image_url},{x},{y},{opacity},{width_scale}
 */
export type WatermarkConfig = {
  imageUrl: string;         // Watermark image URL (share.branding.logo_url)
  placement?: string;       // Placement preset, default 'center'
  scale?: number;           // Width scale percentage, default 20
  opacity?: number;         // 0 (opaque) to 100 (transparent), default 40
};

const PLACEMENT_MAP: Record<string, [string, string]> = {
  'left-top': ['0', '0'],
  'right-top': ['-0', '0'],
  'left-bottom': ['0', '-0'],
  'right-bottom': ['-0', '-0'],
  'center': ['center', 'center'],
  'repeat': ['repeat', 'repeat'],
};

/**
 * Build the watermark query parameter value for ODI URLs.
 */
export const buildWatermarkParam = (wm: WatermarkConfig): string => {
  const [x, y] = PLACEMENT_MAP[wm.placement || 'center'] || ['center', 'center'];
  const opacity = wm.opacity ?? 40;
  const scale = wm.scale ?? 20;
  return `${wm.imageUrl},${x},${y},${opacity},${scale}`;
};

/**
 * Get thumbnail URL from ODI (On-Demand Image) shared_links configuration.
 * Used for public share pages where assets have pre-signed ODI URLs.
 * Optionally applies a watermark overlay (e.g. when only branded ODI is enabled).
 */
type GetODIThumbnailParams = {
  odi: ShareAssetODI;
  width?: number;
  height?: number;
  format?: string;
  watermark?: WatermarkConfig;
};

export const getODIThumbnailUrl = ({
  odi,
  width = 300,
  height = 300,
  format,
  watermark,
}: GetODIThumbnailParams): string => {
  // Clamp dimensions to max_dimension
  const maxDim = odi.max_dimension || 2000;
  const clampedWidth = Math.min(width, maxDim);
  const clampedHeight = Math.min(height, maxDim);

  // Build URL with query params. Omit format when not explicitly requested so
  // the ODI endpoint serves the asset in its native format — this preserves
  // alpha channel (e.g. background-removed PNGs) in share-page thumbnails.
  const separator = odi.base_url.includes('?') ? '&' : '?';
  let url = `${odi.base_url}${separator}resize=${clampedWidth},${clampedHeight}`;

  if (format) {
    // Fall back to the first allowed format if the requested one is disallowed.
    const effectiveFormat = odi.allowed_formats.includes(format)
      ? format
      : odi.allowed_formats[0];
    if (effectiveFormat) {
      url += `&format=${effectiveFormat}`;
    }
  }

  if (watermark) {
    url += `&watermark=${encodeURIComponent(buildWatermarkParam(watermark))}`;
  }

  return url;
};

/**
 * Get ODI download URL with face-aware cropping.
 * Used for downloading images at specific sizes for social media etc.
 * Optionally applies a watermark overlay.
 */
type GetODIDownloadParams = {
  odi: ShareAssetODI;
  width: number;
  height: number;
  format: string;
  filename?: string;
  align?: string;
  watermark?: WatermarkConfig;
};

export const getODIDownloadUrl = ({
  odi,
  width,
  height,
  format,
  filename,
  align = 'face',
  watermark,
}: GetODIDownloadParams): string => {
  // Clamp dimensions to max (2048x1536)
  const clampedWidth = Math.min(width, 2048);
  const clampedHeight = Math.min(height, 1536);
  
  // Use first allowed format if requested format is not allowed
  const effectiveFormat = odi.allowed_formats.includes(format)
    ? format
    : odi.allowed_formats[0] || 'jpg';
  
  const separator = odi.base_url.includes('?') ? '&' : '?';
  let url = `${odi.base_url}${separator}crop=${clampedWidth},${clampedHeight},${align}&format=${effectiveFormat}&delivery=download`;
  
  if (filename) {
    url += `&dl_prefix=${encodeURIComponent(filename)}`;
  }
  
  if (watermark) {
    url += `&watermark=${encodeURIComponent(buildWatermarkParam(watermark))}`;
  }
  
  return url;
};

export const isPreviewPlayable = (datatype: string) => {
  return ['video', 'audio'].includes(datatype.split('/')[0]);
};

// ---------------------------------------------------------------------------
// Permanent public CDN URLs + embed snippets (#932). These are the stable
// links users paste into a CMS — no JWT, no _ctx, no expiry. They only work
// for PUBLIC conversions (the per-conversion `public` flag); private ones
// must keep using signed get_link URLs.
// ---------------------------------------------------------------------------

/** Public video/audio transcode CDN URL. HLS gets the playlist. */
export const getPublicTranscodeUrl = (assetId: string, conversionKey: string): string => {
  if (conversionKey === 'hls-video') {
    return `${config.cdnUrl}/api/v1/assets/${assetId}/transcodes/playlist.m3u8`;
  }
  return `${config.cdnUrl}/api/v1/assets/${assetId}/transcodes/${conversionKey}.mp4`;
};

/** Permanent CDN URL for a named (stored) conversion file, e.g. a
 *  Photoshop edit or print export. Only works when the conversion is public. */
export const getPublicNamedConversionUrl = (assetId: string, conversionKey: string): string =>
  `${config.cdnUrl}/api/v1/assets/${assetId}/conversions/${conversionKey}`;

export type PublicImageUrlParams = {
  id: string;
  width: number;
  height: number;
  format?: string; // jpg | png | webp — omit for content negotiation
  quality?: number; // 1-100
  /** Resize a named conversion (e.g. `ai_restore`) instead of the asset's
   *  default smart image: /conversions/{key}?resize=... */
  conversionKey?: string;
};

/** Permanent resized-image CDN URL (On-Demand Image endpoint, metered). */
export const getPublicImageConversionUrl = ({
  id,
  width,
  height,
  format,
  quality,
  conversionKey,
}: PublicImageUrlParams): string => {
  // ODI caps resizes at 2048x1536 server-side; clamp so the copied URL
  // returns what the user asked for instead of a silently smaller image.
  const clampedWidth = Math.min(width, 2048);
  const clampedHeight = Math.min(height, 1536);
  const path = conversionKey
    ? `/api/v1/assets/${id}/conversions/${conversionKey}`
    : `/api/v1/assets/${id}/conversions`;
  let url = `${config.cdnUrl}${path}?resize=${clampedWidth},${clampedHeight}`;
  if (format) url += `&format=${format}`;
  if (quality) url += `&quality=${quality}`;
  return url;
};

/** iframe snippet for the CDN-hosted video embed page. The page is a static
 *  Video.js player served straight from the CDN origin (filespin/static in
 *  the backend), so embeds never touch the dashboard app. */
export const buildIframeEmbedSnippet = (assetId: string, conversionKey: string): string => {
  const src = `${config.cdnUrl}/embed/video/${assetId}?conversion=${conversionKey}`;
  return (
    `<iframe src="${src}" width="640" height="360" frameborder="0" ` +
    `allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`
  );
};

/** <img> snippet for a public resized-image CDN URL. */
export const buildImgEmbedSnippet = (url: string, alt = ''): string =>
  `<img src="${url}" alt="${alt.replace(/"/g, '&quot;')}" loading="lazy" />`;

export const addCacheBuster = (url: string): string => {
  if (!url) return url;
  
  const separator = url.includes('?') ? '&' : '?';
  const timestamp = Date.now();
  return `${url}${separator}_cb=${timestamp}`;
};
